import React, { FC, memo, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import * as yup from 'yup';
import omit from 'lodash/omit';

import GeneralDialogForm from '../../../../components/formHelpers/GeneralDialogForm';
import { ToggleDialog } from '../../../../components/Tables/TableWithModalEdit/modalFormPieces/IFormModal';
import { displayToast } from '../../../../components/RTToast';
import { BillingContactPDFType } from '../../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { documentTypesEnum } from '../../../../api/vouchers/helpers';
import { Voucher, voucherCertificationTypesEnum } from '../../../../api/vouchers/types';
import { Context as VouchersContext } from '../../../../api/vouchers/context';
import { Context as CompaniesContext } from '../../../../api/companies/context';
import { getPdfCompanyInfo } from '../../../../api/companies/helpers';
import { Context as AuthContext } from '../../../../api/auth/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Context as SupplierAccountsContext } from '../../../../api/supplierAccounts/context';
import { getPdfSupplierInfo } from '../../../../api/supplierAccounts/helpers';
import { Context as TravelAgencyContext } from '../../../../api/travelAgencyAccounts/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Client } from '../../../../api/clients/types';
import { getPdfAgentInfo } from '../../../../api/travelAgencyAccounts/helpers';
import { getDataNameAnd_id, getDataOptions } from '../../../../utils/arrayUtils';
import { Currency } from '../../../../api/currencies/types';
import {
    voucherSourceTargetTypesEnum,
    voucherSourceTargetTypesToPreviewEnum
} from '../../../../api/travelFiles/constants/voucherSourceTarget';
import { travelFileTypesEnum } from '../../../../api/travelFiles/constants/travelFileTypesOptions';
import { TravelFilePassengerFlatten } from '../../../Reservations/TravelFile/TravelFileHomePage/PassengersSection/PassengersSection';
import ViewVoucherPdf from '../../../Reservations/TravelFile/TravelFileHomePage/ViewVoucherPdf';
import { getCreditDebitPdfTableData } from '../../../../components/Pdfs/Vouchers/CreditDebitPdfHelpers';

import CreditDebitNoteForm from './CreditDebitNoteForm';
import { PreReceiptState } from '../Receipt/Receipt';
import { OnAddVoucher } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/AccountingButtons';

type Props = {
    open: boolean;
    toggleOpen: ToggleDialog;
    documentGenerationData: Partial<Voucher> | null;
    creditOrDebit: 'Credit' | 'Debit';
    isCreditNoteNeeded?: boolean;
    autoCreditNoteData?: PreReceiptState;
    bankAccountObject?: Partial<Voucher>;
    setOpenModal?: Function | any;
    onAddVoucher?: OnAddVoucher | any;
};
const validationSchema = yup.object().shape({
    for: yup.string(),
    amount: yup.number().required('Amount is required').moreThan(0, 'Amount should be greater than zero'),
    supplierName: yup.string(),
    travelAgentName: yup.string(),
    currency: yup.string().nullable().required('Currency is required'),
    remark: yup.string(),
    dueDate: yup.date().nullable()
});
//todo:change this type to be more specific to use Yup get types
export type PreCreditDebitNoteState = {
    for: string;
    beneficiary: string;
    dueDate: Date | null;
    amount: number;
    currency: string | Currency;
    remark: string;
};

const CreditDebitNote: FC<Props> = (props: Props) => {
    const {
        open,
        toggleOpen,
        documentGenerationData,
        creditOrDebit,
        isCreditNoteNeeded = false,
        autoCreditNoteData,
        bankAccountObject,
        setOpenModal,
        onAddVoucher
    } = props;

    const DOCUMENT_TYPE =
        creditOrDebit === 'Credit' ? documentTypesEnum.CREDIT_DEBIT : documentTypesEnum.DEBIT_CREDIT;
    const [creditNote, setCreditNote] = useState<Voucher | null>(null);

    const clientsContext = useContext(ClientsContext);
    const companiesContext = useContext(CompaniesContext);
    const travelAgencyContext = useContext(TravelAgencyContext);
    const supplierAccountsContext = useContext(SupplierAccountsContext);
    const currenciesContext = useContext(CurrenciesContext);
    const authContext = useContext(AuthContext);
    const vouchersContext = useContext(VouchersContext);

    useEffect(() => {
        if (isCreditNoteNeeded && autoCreditNoteData) {
            postDataToDB({
                for: autoCreditNoteData?.for || '',
                beneficiary: autoCreditNoteData?.beneficiary || '',
                dueDate: new Date(),
                amount: autoCreditNoteData?.amountDiff || 0,
                currency: autoCreditNoteData?.currency || '',
                remark: autoCreditNoteData?.remark || ''
            });
        }
    }, [autoCreditNoteData, isCreditNoteNeeded]);

    // Static lookup data is prefetched at login (usePrefetchStaticData)
    const companyInfo = useMemo(() => getPdfCompanyInfo(companiesContext), [companiesContext]);

    // The beneficiary is the target/credit account in a credit note
    // or the source/debit account in a debit note:
    const beneficiary =
        typeof documentGenerationData?.info?.pdfInfo?.beneficiary === 'string'
            ? documentGenerationData?.info?.pdfInfo?.beneficiary
            : documentGenerationData?.info?.pdfInfo?.beneficiary?.name;

    // This is the target currency (may be a full object with .code, or just an ID):
    const initialCurrency = useMemo(() => {
        const curr = documentGenerationData?.currency;
        if (!curr) return undefined;
        const options = getDataOptions(currenciesContext.data);
        if (typeof curr === 'object') return options.find((c) => c.code === (curr as Currency).code);
        return options.find((c) => c._id == curr);
    }, [currenciesContext.data, documentGenerationData]);

    // This is the source currency (may be a full object with .code, or just an ID):
    const sourceCurrency = useMemo(() => {
        const curr = documentGenerationData?.info?.currency;
        if (!curr) return undefined;
        const options = getDataOptions(currenciesContext.data);
        if (typeof curr === 'object') return options.find((c) => c.code === curr.code);
        return options.find((c) => c._id == curr);
    }, [currenciesContext.data, documentGenerationData]);

    let supplierInfo: null | BillingContactPDFType = null,
        agentInfo: null | BillingContactPDFType = null;
    if (creditOrDebit === 'Credit') {
        if (
            documentGenerationData?.targetType === voucherSourceTargetTypesEnum.SUPPLIER &&
            documentGenerationData.targetId
        ) {
            const selectedSupplier = getDataOptions(supplierAccountsContext.data).find(
                (supplier) => supplier._id === documentGenerationData.targetId
            );
            supplierInfo = getPdfSupplierInfo(selectedSupplier);
        }

        if (
            documentGenerationData?.targetType === voucherSourceTargetTypesEnum.AGENT &&
            documentGenerationData.targetId
        ) {
            const selectedAgent = getDataOptions(travelAgencyContext.data).find(
                (agent) => agent._id === documentGenerationData.targetId
            );
            agentInfo = getPdfAgentInfo(selectedAgent);
        }
    } else {
        if (
            documentGenerationData?.sourceType === voucherSourceTargetTypesEnum.SUPPLIER &&
            documentGenerationData.sourceId
        ) {
            const selectedSupplier = getDataOptions(supplierAccountsContext.data).find(
                (supplier) => supplier._id === documentGenerationData.sourceId
            );
            supplierInfo = getPdfSupplierInfo(selectedSupplier);
        }

        if (
            documentGenerationData?.sourceType === voucherSourceTargetTypesEnum.AGENT &&
            documentGenerationData.sourceId
        ) {
            const selectedAgent = getDataOptions(travelAgencyContext.data).find(
                (agent) => agent._id === documentGenerationData.sourceId
            );
            agentInfo = getPdfAgentInfo(selectedAgent);
        }
    }

    let passenger: (TravelFilePassengerFlatten & { name: string }) | null = null;
    if (
        (documentGenerationData?.targetType === (travelFileTypesEnum.GROUP || travelFileTypesEnum.CLIENT) ||
            documentGenerationData?.sourceType ===
                (travelFileTypesEnum.GROUP || travelFileTypesEnum.CLIENT)) &&
        typeof documentGenerationData?.info?.pdfInfo?.beneficiary === 'object'
    ) {
        passenger = documentGenerationData?.info?.pdfInfo.beneficiary;
    }

    // Form component props:
    const initialValues: PreCreditDebitNoteState = useMemo(() => {
        return {
            for: 'Balance travel file',
            beneficiary: beneficiary,
            amount: documentGenerationData?.info?.amount || '',
            currency: initialCurrency || '',
            remark: '',
            dueDate: new Date()
        };
    }, [documentGenerationData, initialCurrency, beneficiary]);

    const postDataToDB = (preCreditNoteState: PreCreditDebitNoteState, actions?: any) => {
        let currencyCode: string = '---';
        if (typeof preCreditNoteState.currency === 'object') {
            currencyCode = preCreditNoteState.currency?.code || '---';
        } else {
            currencyCode = currenciesContext.getCodeByCurrency(preCreditNoteState.currency);
        }
        // For the case the beneficiary is an account:
        const topLeftDetailsText =
            (documentGenerationData?.targetType === voucherSourceTargetTypesEnum.ACCOUNT &&
                creditOrDebit === 'Credit') ||
            (documentGenerationData?.sourceType === voucherSourceTargetTypesEnum.ACCOUNT &&
                creditOrDebit === 'Debit')
                ? preCreditNoteState.beneficiary
                : null;

        const tableDataSource = () => {
            switch (documentGenerationData?.sourceType) {
                case voucherSourceTargetTypesEnum.SUPPLIER: {
                    return documentGenerationData.info?.supplierName;
                }
                case voucherSourceTargetTypesEnum.AGENT: {
                    return documentGenerationData.info?.travelAgentName;
                }
                case voucherSourceTargetTypesEnum.ACCOUNT: {
                    return documentGenerationData.info?.accountName;
                }
                default: {
                    return `${voucherSourceTargetTypesToPreviewEnum[documentGenerationData?.sourceType!]} #${
                        documentGenerationData?.sourceNumber
                    }`;
                }
            }
        };
        const tableDataTarget = () => {
            switch (documentGenerationData?.targetType) {
                case voucherSourceTargetTypesEnum.SUPPLIER: {
                    return creditOrDebit === 'Credit'
                        ? preCreditNoteState.beneficiary
                        : getDataNameAnd_id(supplierAccountsContext.data, 'supplierCompanyName').find(
                              (supplier) => supplier._id === documentGenerationData.targetId
                          )?.name || '---';
                }
                case voucherSourceTargetTypesEnum.AGENT: {
                    return creditOrDebit === 'Credit'
                        ? preCreditNoteState.beneficiary
                        : getDataNameAnd_id(travelAgencyContext.data, 'companyName').find(
                              (agent) => agent._id === documentGenerationData.targetId
                          )?.name || '---';
                }
                case voucherSourceTargetTypesEnum.ACCOUNT: {
                    // Account targets are selected from account dropdowns; use the chosen
                    // account name (beneficiary) rather than falling back to GL number.
                    return (
                        preCreditNoteState.beneficiary ||
                        documentGenerationData?.info?.accountName ||
                        `${voucherSourceTargetTypesToPreviewEnum[documentGenerationData?.targetType!]} #${
                            documentGenerationData?.targetNumber
                        }`
                    );
                }
                default: {
                    return `${voucherSourceTargetTypesToPreviewEnum[documentGenerationData?.targetType!]} #${
                        documentGenerationData?.targetNumber
                    }`;
                }
            }
        };
        const { columns, rows, summaryLine } = getCreditDebitPdfTableData({
            source: tableDataSource(),
            target: tableDataTarget(),
            amount: Number(preCreditNoteState.amount),
            currencyCode
        });
        const pdfInfo = {
            creditOrDebit,
            topLeftDetailsText,
            companyInfo,
            supplierInfo,
            agentInfo,
            passenger,
            columns,
            rows,
            summaryLine,
            userName: (authContext.currUser as any)?.username || authContext.currUser?.name || 'No User Name',
            voucherCertification: voucherCertificationTypesEnum.COPY,
            pdfImageSource: (clientsContext.selected as Client)?.companyLogoUrl
        };

        vouchersContext
            .addDocument({
                type: DOCUMENT_TYPE,
                sourceId: documentGenerationData?.sourceId,
                sourceType: documentGenerationData?.sourceType,
                sourceNumber: documentGenerationData?.sourceNumber,
                travelFileId: documentGenerationData?.travelFileId,
                travelFileType: documentGenerationData?.travelFileType || 'client',
                targetId:
                    (autoCreditNoteData ? bankAccountObject?.targetId : documentGenerationData?.targetId) ||
                    documentGenerationData?.sourceId,
                targetType: autoCreditNoteData
                    ? bankAccountObject?.targetType
                    : documentGenerationData?.targetType,
                targetNumber: autoCreditNoteData
                    ? bankAccountObject?.targetNumber
                    : documentGenerationData?.targetNumber,
                info: {
                    ...omit(preCreditNoteState, ['beneficiary', 'currency']),
                    currency: preCreditNoteState.currency as Currency,
                    supplierName: documentGenerationData?.info?.supplierName,
                    travelAgentName: documentGenerationData?.info?.travelAgentName,
                    accountName: documentGenerationData?.info?.accountName,
                    travelFileNumber: documentGenerationData?.info?.travelFileNumber,
                    isCovering: documentGenerationData?.info?.isCovering,
                    coveredBy: [],
                    pdfRows: {
                        columns,
                        rows,
                        summaryLine
                    },
                    // Store passenger for documentpassenger creation
                    passenger: passenger
                        ? {
                              title: (passenger as any).title || '',
                              name:
                                  (passenger as any).name ||
                                  `${(passenger as any).firstName || ''} ${(passenger as any).lastName || ''}`.trim(),
                              address: (passenger as any).address || '',
                              city: (passenger as any).city || '',
                              country: (passenger as any).country || '',
                              postalCode: (passenger as any).postalCode || ''
                          }
                        : null,
                    pdfInfo
                },
                isPaid: false,
                currency: sourceCurrency
            })
            .then((newCreditNote) => {
                // pdfInfo is not persisted in DB — merge it back so ViewVoucherPdf can render
                setCreditNote({ ...newCreditNote, info: { ...newCreditNote.info, pdfInfo } });
            })
            .catch((err) => {
                displayToast(
                    'error',
                    `Error while creating ${DOCUMENT_TYPE}`,
                    err.response?.data?.message || 'Something went wrong'
                );
                throw err;
            })
            .finally(() => {
                actions?.setSubmitting(false);
            });
    };

    // Handle creating a new credit/debit document:
    const handleSubmit = useCallback(
        (preCreditNoteState: PreCreditDebitNoteState, actions: any) => {
            postDataToDB(preCreditNoteState, actions);
        },
        [
            documentGenerationData,
            authContext.currUser,
            companyInfo,
            supplierInfo,
            agentInfo,
            vouchersContext,
            currenciesContext,
            clientsContext.selected,
            creditOrDebit,
            passenger,
            sourceCurrency,
            supplierAccountsContext.data,
            travelAgencyContext.data,
            DOCUMENT_TYPE
        ]
    );

    return (
        <>
            <GeneralDialogForm
                open={open}
                title={DOCUMENT_TYPE}
                onClose={toggleOpen}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                maxWidth="sm"
                PaperProps={{ sx: { maxWidth: 560 } }}
                minHeight="600px"
                hasCancelButton
                actionText={`Generate ${DOCUMENT_TYPE}`}
            >
                <CreditDebitNoteForm creditOrDebit={creditOrDebit} />
            </GeneralDialogForm>
            {!!creditNote && (
                <ViewVoucherPdf
                    voucherProps={creditNote}
                    toggleVoucherModalOpen={() => {
                        setCreditNote(null);
                        toggleOpen(false);
                        setOpenModal && setOpenModal(false);
                        onAddVoucher && onAddVoucher();
                    }}
                />
            )}
        </>
    );
};

export default memo(CreditDebitNote);
